import type { ReportId } from "@/features/today"

/**
 * Fallback report PDF source (preview sample).
 *
 * The real report url is the backend's (`GET /v1/reports/me` → `url`); the viewer
 * uses that. This bundled sample is only a fallback for reports that still have
 * no server entry. Love Match is backend-ONLY (no fallback) — the viewer waits
 * for its server url instead of resolving anything here.
 */
const STUB_PDF_URL = "/reports/astrology-report-sample.pdf"

/** Per-report fallback overrides. Reports not listed use {@link STUB_PDF_URL}. */
const PDF_BY_ID: Partial<Record<ReportId, string>> = {}

/** Resolve the fallback PDF source for a report (not used for backend-only reports). */
export function getReportPdfUrl(reportId: ReportId): string {
  return PDF_BY_ID[reportId] ?? STUB_PDF_URL
}

/** Human-readable file size, e.g. "3.2MB" / "812KB". */
export function formatFileSize(bytes: number): string {
  if (!bytes) return ""
  const mb = bytes / (1024 * 1024)
  if (mb >= 1) return `${mb.toFixed(1)}MB`
  return `${Math.round(bytes / 1024)}KB`
}

function isIos(): boolean {
  if (typeof navigator === "undefined") return false
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return true
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1
}

/**
 * Trigger a browser download of the (full) report PDF.
 * iOS Safari ignores `<a download>` for blob URLs and navigates instead —
 * use the system share sheet so the user can Save to Files.
 */
export async function downloadReportPdf(
  pdfUrl: string,
  fileName: string,
  blob?: Blob | null,
): Promise<void> {
  if (isIos() && typeof navigator.share === "function") {
    try {
      const fileBlob = blob instanceof Blob ? blob : await fetch(pdfUrl).then((r) => r.blob())
      const file = new File([fileBlob], fileName, { type: "application/pdf" })
      if (!navigator.canShare || navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] })
        return
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return
    }
  }

  const a = document.createElement("a")
  a.href = pdfUrl
  a.download = fileName
  a.rel = "noopener"
  document.body.appendChild(a)
  a.click()
  a.remove()
}
